/**
 * The console chrome — sidebar, top bar, content well.
 *
 * Replaces the group's `@theg1group/app-shell`, which this repo can no longer
 * install. The layout, class names and token usage are carried over verbatim so
 * the console still looks like the rest of G1; what is deliberately gone is the
 * part an outside team should not have: the ⌘G launcher over every internal app,
 * the cross-app registry, and the 2FA gate (a sign-in concern, settled by
 * g1-auth before a token exists).
 *
 * Identity is passed in as a prop rather than read from a context. `App` has
 * already loaded standing to decide which console to render, so a provider would
 * only be a second copy of what the caller is holding.
 */
import { ChevronDown, ChevronLeft, ChevronRight, LogOut, Menu, X } from "lucide-react";
import { type ComponentType, type ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

export interface NavItem {
	label: string;
	path: string;
	group: string;
}

export interface ShellProps {
	title: string;
	Icon?: ComponentType<{ size?: number }>;
	nav: readonly NavItem[];
	user: { email: string | null; role: string };
	onSignOut: () => void;
	children: ReactNode;
}

const COLLAPSED_KEY = "g1_sidebar_collapsed";
const EXPANDED_KEY = "g1_nav_expanded";

const itemBase =
	"flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors";

function navClass(active: boolean, collapsed: boolean): string {
	const state = active
		? "bg-primary-soft text-primary-soft-fg"
		: "text-muted hover:bg-surface-2 hover:text-content";
	return `${itemBase} ${state} ${collapsed ? "justify-center px-0" : ""}`;
}

/** localStorage throws in private-mode Safari and behind a blocked third-party
 *  cookie policy; a remembered rail is never worth a blank console. */
function readStored<T>(key: string, fallback: T): T {
	try {
		const raw = localStorage.getItem(key);
		return raw === null ? fallback : (JSON.parse(raw) as T);
	} catch {
		return fallback;
	}
}

function writeStored(key: string, value: unknown): void {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch {
		/* ignore */
	}
}

function useGroups(nav: readonly NavItem[]): Array<{ label: string; items: NavItem[] }> {
	const out: Array<{ label: string; items: NavItem[] }> = [];
	for (const item of nav) {
		const existing = out.find((g) => g.label === item.group);
		if (existing) existing.items.push(item);
		else out.push({ label: item.group, items: [item] });
	}
	return out;
}

/**
 * Pull the palette from g1-auth at runtime.
 *
 * `src/theme.css` is a copy taken when we stopped installing the shared package,
 * so it will drift. This <link> is appended after the bundle, and with equal
 * specificity the later `:root` wins — so a central palette change still reaches
 * this console on the next load, and the bundled copy is only the offline
 * fallback. It is the one piece of the design system that still updates itself.
 */
function useRuntimeTheme(): void {
	useEffect(() => {
		const id = "g1-runtime-theme";
		if (document.getElementById(id)) return;
		const link = document.createElement("link");
		link.id = id;
		link.rel = "stylesheet";
		link.href = "https://auth.g1.network/theme";
		document.head.appendChild(link);
	}, []);
}

export function Shell({ title, Icon, nav, user, onSignOut, children }: ShellProps) {
	const location = useLocation();
	const groups = useGroups(nav);
	useRuntimeTheme();

	const [collapsed, setCollapsed] = useState(() => readStored(COLLAPSED_KEY, false));
	const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
		readStored<Record<string, boolean>>(EXPANDED_KEY, {}),
	);
	const [mobileOpen, setMobileOpen] = useState(false);

	useEffect(() => writeStored(COLLAPSED_KEY, collapsed), [collapsed]);
	useEffect(() => writeStored(EXPANDED_KEY, expanded), [expanded]);

	const closeMobile = () => setMobileOpen(false);
	const hasActive = (items: NavItem[]) =>
		items.some((i) => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`));

	return (
		<div className="flex min-h-screen bg-canvas text-content">
			{mobileOpen && (
				<button
					type="button"
					className="fixed inset-0 z-30 bg-black/50 md:hidden"
					onClick={closeMobile}
					aria-label="Close menu"
				/>
			)}

			<aside
				className={`${collapsed ? "w-14" : "w-56"} fixed top-0 left-0 z-40 flex h-screen flex-shrink-0 flex-col border-r border-line bg-surface transition-all duration-200 md:sticky ${
					mobileOpen ? "translate-x-0" : "-translate-x-full"
				} md:translate-x-0`}
			>
				<div
					className={`flex h-14 flex-shrink-0 items-center gap-2.5 border-b border-line px-4 ${
						collapsed ? "justify-center px-2" : "justify-between"
					}`}
				>
					<div className="flex min-w-0 items-center gap-2.5">
						{Icon ? (
							<span className="flex-shrink-0 text-primary">
								<Icon size={20} />
							</span>
						) : null}
						{!collapsed && <span className="truncate text-sm font-bold text-content">{title}</span>}
					</div>
					{!collapsed && (
						<button
							type="button"
							onClick={closeMobile}
							className="rounded p-1 text-muted hover:text-content md:hidden"
							aria-label="Close menu"
						>
							<X size={16} />
						</button>
					)}
				</div>

				<nav className="flex-1 space-y-3 overflow-y-auto p-2">
					{groups.map((group) => {
						const open = expanded[group.label] !== false || hasActive(group.items);
						return (
							<div key={group.label}>
								{!collapsed && (
									<button
										type="button"
										onClick={() =>
											setExpanded((p) => ({ ...p, [group.label]: p[group.label] === false }))
										}
										className="flex w-full items-center justify-between rounded px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted hover:text-content"
									>
										<span>{group.label}</span>
										<ChevronDown
											size={12}
											className={`transition-transform ${open ? "" : "-rotate-90"}`}
										/>
									</button>
								)}
								{(open || collapsed) && (
									<div className="mt-0.5 space-y-0.5">
										{group.items.map((item) => (
											<NavLink
												key={item.path}
												to={item.path}
												// Without `end`, "/" is a prefix of every route and Overview
												// stays highlighted on every screen.
												end={item.path === "/"}
												onClick={closeMobile}
												title={collapsed ? item.label : undefined}
												className={({ isActive }) => navClass(isActive, collapsed)}
											>
												{collapsed ? (
													<span aria-hidden>{item.label.charAt(0)}</span>
												) : (
													<span className="truncate">{item.label}</span>
												)}
											</NavLink>
										))}
									</div>
								)}
							</div>
						);
					})}
				</nav>

				<button
					type="button"
					onClick={() => setCollapsed((c) => !c)}
					className="mx-2 mb-1 hidden h-8 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-content md:flex"
					title={collapsed ? "Expand" : "Collapse"}
				>
					{collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
				</button>
			</aside>

			<div className="flex min-w-0 flex-1 flex-col">
				<header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-surface px-4">
					<button
						type="button"
						onClick={() => setMobileOpen(true)}
						className="text-content md:hidden"
						aria-label="Open menu"
					>
						<Menu size={22} />
					</button>
					<span className="font-semibold text-content md:hidden">{title}</span>

					<div className="ml-auto flex items-center gap-3">
						<div className="hidden text-right leading-tight sm:block">
							<div className="truncate text-xs font-medium text-content">{user.email}</div>
							<div className="truncate text-[10px] text-muted">{user.role}</div>
						</div>
						<button
							type="button"
							onClick={onSignOut}
							className="rounded p-1.5 text-muted hover:bg-surface-2 hover:text-content"
							title={user.email ? `Sign out (${user.email})` : "Sign out"}
						>
							<LogOut size={16} />
						</button>
					</div>
				</header>

				<main className="min-w-0 flex-1 overflow-auto bg-canvas">{children}</main>
			</div>
		</div>
	);
}
